import React from 'react';
import { LinkIcon } from '@asigna/kit';
import Box from '@mui/material/Box';
import { addressToString, PostConditionMode } from '@stacks/transactions';
import BigNumber from 'bignumber.js';
import CopyIcon from 'images/copy_icon.svg';
import DefIcon from 'images/def_icon.svg';
import StxIcon from 'images/stacks-stx-logo.svg';
import { getTxnExplorerUrl } from 'utils/helper';

import { assets } from '../../../../constants/assets';
import useNetwork from '../../../../hooks/use-network';
import useSafe from '../../../../hooks/use-safe';
import {
  getAmountFromPostCondition,
  getNameFromPostCondition,
  getPostConditionTitle,
} from '../../../../utils/post-condition.utils';
import { IMultisigTransaction } from '../../../../utils/transform-multisig-transaction';
import { useTransactionDetailStyles } from '../../useTransactionDetailStyles';
import { useTokenData } from '../useTokenData';

import { usePostConditionStyles } from './usePostConditionStyles';

const getIcon = (symbol: string) => {
  if (symbol === 'STX') {
    return StxIcon;
  }
  const cachedMetadata = assets.find((asset) => asset.symbol === symbol);

  return cachedMetadata ? cachedMetadata.image || DefIcon : DefIcon;
};

const format = (address: string) => {
  return address.slice(0, 4) + '...' + address.slice(-4);
};

const PostConditionItem: React.FC<{ pc: any }> = ({ pc }) => {
  const { safe } = useSafe();
  const classes = usePostConditionStyles();
  const amount = getAmountFromPostCondition(pc);
  const name = getNameFromPostCondition(pc);
  const pcPrincipal = addressToString(pc.principal.address);
  const condition = getPostConditionTitle(pc);
  const isNotStx = 'assetInfo' in pc;
  const assetInfo = pc['assetInfo'];
  const symbol = assetInfo ? assetInfo.assetName.content.toUpperCase() : 'STX';
  const assetAddr = assetInfo && addressToString(assetInfo.address);
  const assetContract = assetInfo && assetInfo.contractName.content;
  const assetPrincipal = isNotStx ? `${format(assetAddr)}.${assetContract}` : '';
  const sender = pcPrincipal === safe.address ? 'You' : 'The contract';
  const token = useTokenData(assetAddr, assetContract);
  const [network] = useNetwork();

  const assetIcon = getIcon(symbol);

  const onCopyClicked = () => {
    navigator.clipboard.writeText(assetPrincipal);
  };

  const formatAmount = (amount: string) => {
    if (!token || !isNotStx) {
      return amount;
    } else {
      return new BigNumber(amount).shiftedBy(-token.decimals).toString();
    }
  };

  return (
    <Box className={classes.pcItem}>
      <span className={classes.pcTitle}>
        {sender} {condition}
      </span>
      <Box className={classes.assetContainer}>
        <img src={assetIcon} width={32} height={32} />
        <span className={classes.assetTitle}>
          {formatAmount(amount)} {symbol}
        </span>
      </Box>
      {isNotStx && (
        <Box className={classes.tokenInfoContainer}>
          <span className={classes.pcSymbol}>{name}</span>
          <div className={classes.tokenContract}>
            <span className={classes.tokenContractText}>{assetPrincipal}</span>
            <img
              alt="copy"
              width={16}
              height={16}
              className={classes.copyIcon}
              src={CopyIcon}
              onClick={(e) => {
                e.stopPropagation();
                onCopyClicked();
              }}
            />

            <LinkIcon href={getTxnExplorerUrl(assetAddr + '.' + assetContract, network)} />
          </div>
        </Box>
      )}
      <Box className={classes.pcMessage}>
        {sender} {condition}{' '}
        <span className={classes.pcMessageAmount}>
          {formatAmount(amount)} {symbol}
        </span>{' '}
        or the transaction will abort.
      </Box>
    </Box>
  );
};
export const PostConditions: React.FC<{ transaction: IMultisigTransaction }> = ({
  transaction,
}) => {
  const postConditions: any[] = transaction.stacksTransaction.postConditions.values;
  const classes = usePostConditionStyles();
  const detailsClasses = useTransactionDetailStyles();
  const postConditionMode = PostConditionMode[transaction.stacksTransaction.postConditionMode];
  const isAllowMode = transaction.stacksTransaction.postConditionMode === PostConditionMode.Allow;

  return (
    <div className={classes.wrapper}>
      <div className={detailsClasses.itemWrapper}>
        <p className={detailsClasses.itemTitle}>Post Conditions Mode</p>
        <p className={detailsClasses.itemContent}>{postConditionMode}</p>
      </div>
      {isAllowMode && (
        <div className={classes.warning}>
          <p className={classes.warningTitle}>This transaction can transfer any of your assets</p>
          <p className={classes.warningText}>
            If you confirm, you allow it to transfer any of your tokens. Only confirm if you trust
            and have verified the contract.
          </p>
        </div>
      )}
      <Box className={classes.container}>
        {postConditions.map((pc, index) => (
          <>
            {index !== 0 && <hr className={classes.separator} />}
            <PostConditionItem pc={pc} key={index} />
          </>
        ))}
      </Box>
    </div>
  );
};
